import type { BaseImagingProvider } from '../ViewportDre/modules/imaging/BaseImagingProvider';
import { MultiObjectImagingProvider } from '../ViewportDre/modules/imaging/MultiObjectImagingProvider';
import { MultiStackImagingProvider } from '../ViewportDre/modules/imaging/MultiStackImagingProvider';
import type {
  Stack,
  ViewportConfiguration,
  ViewportsConfigurations,
} from '../ViewportsConfigurations/types';
import type { TStackProviders } from './ViewerContext';
import { viewportIdToConfig } from './viewerUtils';

/**
 * Every imaging provider referenced by a viewport in `viewerId`'s window, including the constituent
 * providers behind a composite or multi-layer stack.
 *
 * The recursion is the important part. A viewport's `stack.smid` can be a synthetic composite or
 * multi-layer smid (prior-series stacking, PET/CT fusion), whose provider is a
 * MultiStack/MultiObject provider that owns no pixels — the real per-series providers hang off it and
 * are keyed in `stackProviders` under their own smids, which appear in no viewport configuration.
 * Anything deciding "is this stack on screen?" from viewport smids alone silently misclassifies those
 * children as off-screen: `useViewerMemoryConfig` would demote them to `None`, and VX-4775 would
 * leave them prefetched cache-only and never promote them, blanking the viewport.
 */
export function collectProvidersInWindowViewports({
  viewportsConfigurations,
  stackProviders,
  viewerId,
}: {
  viewportsConfigurations: Nullable<ViewportsConfigurations>;
  stackProviders: TStackProviders;
  viewerId: Nullable<string>;
}): Set<BaseImagingProvider<Stack>> {
  return collectProvidersInViewports({
    viewportsConfigurations,
    stackProviders,
    includeViewport: (viewportId) => viewportIdToConfig(viewportId).windowId === viewerId,
  });
}

/** Like {@link collectProvidersInWindowViewports}, for whichever viewports `includeViewport` selects. */
export function collectProvidersInViewports({
  viewportsConfigurations,
  stackProviders,
  includeViewport,
}: {
  viewportsConfigurations: Nullable<ViewportsConfigurations>;
  stackProviders: TStackProviders;
  includeViewport: (viewportId: string, viewportConfiguration: ViewportConfiguration) => boolean;
}): Set<BaseImagingProvider<Stack>> {
  const providersInViewports = new Set<BaseImagingProvider<Stack>>();
  if (viewportsConfigurations == null) return providersInViewports;

  const addWithConstituents = (provider: BaseImagingProvider<Stack>) => {
    if (providersInViewports.has(provider)) return;
    providersInViewports.add(provider);
    if (
      provider instanceof MultiStackImagingProvider ||
      provider instanceof MultiObjectImagingProvider
    ) {
      for (const child of provider.getConstituentProviders()) addWithConstituents(child);
    }
  };

  Object.entries(viewportsConfigurations).forEach(([viewportId, viewportConfiguration]) => {
    if (viewportConfiguration == null || !includeViewport(viewportId, viewportConfiguration))
      return;
    const smid = viewportConfiguration?.stack?.smid;
    const provider = smid ? stackProviders.get(smid) : undefined;
    if (provider) addWithConstituents(provider);
  });

  return providersInViewports;
}
